import React from "react";
import { getGeometry, getShapeKey, colorForAnno } from "../utils/annotationShapes";
import { showTip, moveTip, hideTip } from "../utils/hoverTooltip";

const SAVED_STROKE = "rgb(0, 255, 0)";
const PREVIEW_STROKE = "rgb(0, 255, 0)";

export const renderShape = (shape, geometry, opts = {}) => {
  if (!shape || !geometry) return null;
  const stroke = opts.preview ? PREVIEW_STROKE : opts.color || SAVED_STROKE;
  const strokeWidth = opts.strokeWidth ?? 2;
  const dash = opts.preview ? "4 4" : undefined;
  // When interactive (not drawing), give saved shapes a transparent fill so the
  // whole interior is a hover target for the tooltip.
  const interactive = opts.interactive && !opts.preview;
  const fill = opts.preview
    ? "rgba(0, 255, 0, 0.18)"
    : interactive
    ? "transparent"
    : "none";
  const common = {
    stroke,
    strokeWidth,
    fill,
    vectorEffect: "non-scaling-stroke",
    strokeDasharray: dash,
    pointerEvents: interactive ? "all" : "none",
  };
  switch (shape) {
    case "rectangle":
      return (
        <rect
          x={geometry.x}
          y={geometry.y}
          width={geometry.width}
          height={geometry.height}
          {...common}
        />
      );
    case "ellipse":
    case "circle": {
      const cx = geometry.x + geometry.width / 2;
      const cy = geometry.y + geometry.height / 2;
      return (
        <ellipse
          cx={cx}
          cy={cy}
          rx={Math.abs(geometry.width / 2)}
          ry={Math.abs(geometry.height / 2)}
          {...common}
        />
      );
    }
    case "arrow": {
      const { x1, y1, x2, y2 } = geometry;
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headLen = opts.headLen ?? 0.025;
      const headX1 = x2 - headLen * Math.cos(angle - Math.PI / 6);
      const headY1 = y2 - headLen * Math.sin(angle - Math.PI / 6);
      const headX2 = x2 - headLen * Math.cos(angle + Math.PI / 6);
      const headY2 = y2 - headLen * Math.sin(angle + Math.PI / 6);
      return (
        <g>
          <line x1={x1} y1={y1} x2={x2} y2={y2} {...common} fill="none" />
          <polyline
            points={`${headX1},${headY1} ${x2},${y2} ${headX2},${headY2}`}
            {...common}
            fill="none"
          />
        </g>
      );
    }
    case "freehand": {
      const pts = (geometry.points || []).map((p) => `${p[0]},${p[1]}`).join(" ");
      return <polyline points={pts} {...common} fill="none" />;
    }
    default:
      return null;
  }
};

const AnnotationShapesSvg = ({
  annotations = [],
  preview,
  viewBox = "0 0 1 1",
  preserveAspectRatio = "none",
  style,
  pointerEvents,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  svgRef,
  shapeOpts,
  interactive = false,
}) => {
  return (
    <svg
      ref={svgRef}
      viewBox={viewBox}
      preserveAspectRatio={preserveAspectRatio}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: pointerEvents || "none",
        ...style,
      }}
    >
      {annotations.map((anno) => {
        const shape = getShapeKey(anno);
        const geometry = getGeometry(anno);
        const node = renderShape(shape, geometry, {
          ...shapeOpts,
          interactive,
          color: colorForAnno(anno),
        });
        if (!node) return null;
        const tip = [anno.name, anno.description].filter(Boolean).join(" — ");
        const hoverProps =
          interactive && tip
            ? {
                onMouseEnter: (e) => showTip(tip, e.clientX, e.clientY),
                onMouseMove: (e) => moveTip(e.clientX, e.clientY),
                onMouseLeave: hideTip,
              }
            : {};
        return (
          <g
            key={anno.id}
            id={`shape-${anno.id}`}
            style={{ cursor: interactive ? "help" : undefined }}
            {...hoverProps}
          >
            {node}
          </g>
        );
      })}
      {preview ? <g id="shape-preview">{renderShape(preview.shape, preview.geometry, { ...shapeOpts, preview: true })}</g> : null}
    </svg>
  );
};

export default AnnotationShapesSvg;
